import { useEffect } from "react";
import { Link } from "react-router-dom";
import { HORSES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { Contact } from "../components/Contact";

export const HorsesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="horses-page" className="bg-[#F9F9F6] pt-32 md:pt-36">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <Reveal className="md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#965C3B]">
              Current Listings
            </p>
            <h1
              data-testid="horses-heading"
              className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] text-[#1A2B22]"
            >
              Horses
              <br />
              <em className="italic font-normal text-[#965C3B]">
                in our barn.
              </em>
            </h1>
          </Reveal>
          <Reveal delay={150} className="md:col-span-5 md:text-right">
            <p className="max-w-md md:ml-auto text-[#1A2B22]/75 leading-relaxed">
              A small, evolving roster of Czech Warmbloods personally selected
              and developed by our program. Inquire below for video, vetting
              records, and trial information.
            </p>
            <Link
              to="/#contact"
              data-testid="horses-cta"
              className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-[#1A2B22] text-[#F9F9F6] text-xs uppercase tracking-[0.24em] hover:bg-[#965C3B] transition-colors"
            >
              Inquire about a horse
              <span aria-hidden>→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Listings */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32 space-y-24 md:space-y-32">
        {HORSES.map((h, i) => (
          <Reveal key={h.slug} delay={i * 80}>
            <article
              data-testid={`horse-${h.slug}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start border-t hairline pt-12"
            >
              {/* Image */}
              <div className="md:col-span-7">
                <div className="overflow-hidden">
                  <img
                    src={h.image}
                    alt={`${h.name} — ${h.color}`}
                    className="w-full aspect-[4/5] md:aspect-[5/6] object-cover transition-transform duration-[1.6s] hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#5C6A61]">
                  AI-rendered editorial portrait based on Quincero's reference photo
                </p>
              </div>

              {/* Detail */}
              <div className="md:col-span-5 md:sticky md:top-32">
                <div className="flex items-center gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#965C3B]" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C6A61]">
                    {h.status}
                  </span>
                </div>
                <h2 className="mt-4 font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] text-[#1A2B22]">
                  {h.name}
                </h2>
                <p className="mt-4 text-[#1A2B22]/80 leading-relaxed max-w-md">
                  {h.summary}
                </p>

                {/* Spec table */}
                <dl className="mt-10 grid grid-cols-2 gap-y-6 gap-x-8 max-w-md">
                  <Spec label="Age" value={h.age} />
                  <Spec label="Color" value={h.color} />
                  <Spec label="Sex" value={h.sex} />
                  <Spec label="Height" value={h.height} />
                  <Spec label="Sire" value={h.sire} />
                  <Spec label="Dam" value={h.dam} />
                  <div className="col-span-2">
                    <Spec label="Direction" value={h.discipline} />
                  </div>
                </dl>

                {/* Highlights */}
                {h.highlights?.length > 0 && (
                  <ul className="mt-10 space-y-3 max-w-md">
                    {h.highlights.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-sm text-[#1A2B22]/85"
                      >
                        <span className="mt-2 w-6 h-px bg-[#965C3B] flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to="/#contact"
                    data-testid={`horse-${h.slug}-inquire`}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-[#1A2B22] text-[#F9F9F6] text-xs uppercase tracking-[0.24em] hover:bg-[#965C3B] transition-colors"
                  >
                    Inquire on {h.name}
                    <span aria-hidden>→</span>
                  </Link>
                </div>
              </div>
            </article>
          </Reveal>
        ))}

        {/* Coming soon strip */}
        <Reveal>
          <div className="border-t hairline pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <p className="font-display text-2xl md:text-3xl tracking-tighter text-[#1A2B22] max-w-xl">
              More prospects arriving soon —
              <em className="italic font-normal text-[#965C3B]">
                {" "}
                tell us what you're looking for.
              </em>
            </p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-3 px-6 py-3 border border-[#1A2B22] text-[#1A2B22] text-xs uppercase tracking-[0.24em] hover:bg-[#1A2B22] hover:text-[#F9F9F6] transition-colors"
            >
              Start a search
              <span aria-hidden>→</span>
            </Link>
          </div>
        </Reveal>
      </section>

      <Contact />
    </main>
  );
};

const Spec = ({ label, value }) => (
  <div className="border-t hairline pt-3">
    <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6A61]">
      {label}
    </p>
    <p className="mt-1 font-display text-xl tracking-tighter text-[#1A2B22]">
      {value || "—"}
    </p>
  </div>
);

export default HorsesPage;
